/**
 * Product-specific state availability configuration
 */

import { UnitedStatesState } from './UnitedStatesState';

// Default available states
export const defaultAvailableStates = Object.entries(UnitedStatesState)
  .filter(([key]) => key !== 'none')
  .map(([, state]) => ({
    value: state.stateAbbreviation,
    label: state.stateName,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Product-specific state availability arrays
export const carescoutCareAssuranceStates = [
  'AK', 'AL', 'AR', 'AZ', 'CT', 'DC', 'DE', 'GA', 'HI', 'IA', 'ID', 'IL',
  'IN', 'KS', 'KY', 'LA', 'MD', 'ME', 'MI', 'MO', 'MS', 'MT', 'WI',
  'NC', 'ND', 'NH', 'NM', 'NV', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WV', 'WY'
] as const;

// Nationwide CareMatters products.
// According to Claude, will update if Evan gives specific instructions
/*'MT' Not avalible yet according to Claude */
export const careMattersStates = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL',
  'GA', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD',
  'ME', 'MI', 'MN', 'MO', 'MS', 'NC', 'ND', 'NE', 'NH', 'NJ',
  'NM', 'NV', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN',
  'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
] as const;

/**
 * CareMatters II single life (careMattersTwo 2919) — careMattersStates plus NY.
 *
 * NY is deliberately NOT in careMattersStates itself, because that array is also
 * the footprint for jointCareMatters (2921) and the New York scrape is single
 * life only: every one of the 5,472 NY rows carries policyType 'Single Life'.
 * Widening the shared array would offer New York on the joint product, which has
 * no NY rates behind it.
 *
 * NY is a separate rate table, not the 49-state Alabama fan-out — see
 * packages/database/scripts/transformCareMattersTwoNYCSV.ts and
 * packages/shared/src/calculations/docs/carematters-ii-new-york/README.md.
 *
 * PREREQUISITE, do not ship this list without it: the apply flow resolves forms
 * by (type, stateCode, fundingPolicyTypeId) in
 * packages/server/src/util/carrierApplicationForms/sql.ts, and falls back to the
 * 'default' row when no state row exists. Until an active
 * ('application-form', 'NY', 2919) row exists in
 * InsuranceCarrierProductPolicyApplicationForms, a New York applicant reaching
 * this picker is served the default Nationwide form set instead of the NY forms
 * (NWLA-583-NY / 653-NY / 654-NY).
 */
export const careMattersTwoStates = [...careMattersStates, 'NY'] as const;

/**
 * Nationwide CareMatters Annuity (careMattersAnnuity 2943, jointCareMattersAnnuity 2944).
 *
 * Deliberately NARROWER than careMattersStates above, and the difference is the
 * point: the annuity chassis is not approved everywhere the life-funded
 * CareMatters products are. Seven states are excluded —
 *
 *   CA, CT, IN, NJ   not approved for this product
 *   MT, NY           not approved for any CareMatters product
 *   DC               excluded on probe evidence, not a carrier list: the
 *                    illustration engine returns an empty payload for all 5
 *                    sampled combinations there (tools/stateParityProbe.js,
 *                    2026-08-13). That is the same signature California gives for
 *                    CareMatters II, and it means "unavailable, or something extra
 *                    is required" — the two are indistinguishable from outside.
 *                    Worth confirming against the carrier's state list; if DC
 *                    turns out to be sellable, this is the entry to revisit.
 *
 * — leaving 44. Do NOT collapse this back into careMattersStates on the grounds
 * that it is "the same carrier": the availability fan-out in
 * packages/database/scripts/create-quote-state-availability-migration.sql is keyed
 * on carrierName alone, which is exactly how these rows wrongly acquired CA/CT/IN/NJ
 * in the first place. That migration cannot express a per-product footprint;
 * remove-carematters-annuity-state-availability.sql corrects it afterwards.
 *
 * The 44 states here are all verified to price identically — every compared field
 * matches the Texas baseline to the cent, so one rate set legitimately covers them.
 */
export const careMattersAnnuityStates = [
  'AK', 'AL', 'AR', 'AZ', 'CO', 'DE', 'FL',
  'GA', 'HI', 'IA', 'ID', 'IL', 'KS', 'KY', 'LA', 'MA', 'MD',
  'ME', 'MI', 'MN', 'MO', 'MS', 'NC', 'ND', 'NE', 'NH',
  'NM', 'NV', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN',
  'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
] as const;

/** The seven states CareMatters Annuity is not sold in, kept explicit for tests and SQL. */
export const careMattersAnnuityExcludedStates = [
  'CA', 'CT', 'DC', 'IN', 'MT', 'NJ', 'NY'
] as const;

// OneAmerica AssetCare products (assetCare and jointAssetCare have same availability)
export const assetCareStates = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'MT', 'FL', 'GA', 'HI', 'IA',
  'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN',
  'MO', 'MS', 'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'OH', 'OK',
  'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI',
  'WV', 'WY'
] as const;

// MT withdrawn; SecureCare III has no remaining approved states. Retired product —
// see RETIRED_POLICY_TYPES in insuranceCarrierFeatureFlags.ts. MT was its last
// state, removed 2026-08-11 (recoverable from "QuoteStateAvailabilityArchive").
export const secureCareThreeStates: readonly string[] = []

/**
 * Securian SecureCare IV. 49 states — CA and NY are the only exclusions.
 *
 * MT added 2026-08-13 after Securian confirmed availability. No new quotes were
 * needed: all 33,920 SecureCare IV quotes are rated in a single state (AL) and
 * fanned out, so the product is priced nationally and a new state is purely an
 * availability row. Applied with
 * packages/database/scripts/add-quote-state-availability.sql.
 *
 * Contrast CareMatters II, where NY genuinely reprices (ratio 0.96-1.59) and had
 * to be scraped separately — do not assume an expansion is free without checking
 * how the product is rated. That script checks for you.
 *
 * Until this array existed, 2941 had no PRODUCT_STATE_AVAILABILITY entry at all,
 * so getProductAvailableStates() returned undefined and the application state
 * picker fell back to every state — offering CA and NY, which are not approved.
 */
export const secureCareFourStates = [
  'AK', 'AL', 'AR', 'AZ', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA',
  'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD',
  'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH',
  'NJ', 'NM', 'NV', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
] as const;

export const moneyGuardFixedAdvantageStates = [
  'AL', 'AK', 'AZ', 'AR', 'AZ', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'MA', 'ME', 'MD',
  'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'PR', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
] as const

// NYL Asset Flex is approved in all 50 states plus DC (ICC-filed interstate compact + state-specific forms)
export const nylAssetFlexStates = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL',
  'GA', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD',
  'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ',
  'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN',
  'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY'
] as const

/**
 * ManhattanLife OmniFlex Short-Term Care.
 *
 * Deliberately narrower than the carrier's filed footprint: the product is sold
 * from imported rate rows, so a state only belongs here once its rates have
 * actually been swept (packages/illustrations: npm run sweep:manhattanlife).
 * Listing a state we have no rows for would surface the payment option and then
 * render an empty quote list.
 */
export const manhattanLifeOmniFlexStcStates = ['CO'] as const;

/** Issue ages the carrier will underwrite (AGT-STC_0426 p.6). */
export const MANHATTAN_LIFE_STC_MIN_ISSUE_AGE = 45;
export const MANHATTAN_LIFE_STC_MAX_ISSUE_AGE = 89;

/**
 * OneAmerica Asset Care 20-Pay — what has actually been SCRAPED, not what the
 * carrier files. The carrier sells 20-Pay across its whole footprint; we hold
 * only a narrow probe of it, so these lists describe our data, and every one of
 * them must widen as more 20-Pay rows land.
 *
 * Same reasoning as manhattanLifeOmniFlexStcStates above: the payment option has
 * to render before any quote fetch can happen, so offering it to a client we
 * have no rows for surfaces the option and then renders an empty quote list.
 *
 * States: the 48 category-1 states (all priced off the single AK sweep) plus CA,
 * which is category 3 and priced on its own. MT (category 2) and NY are excluded
 * — no 20-Pay rows exist for either.
 */
export const assetCare20PayStates = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL',
  'GA', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA',
  'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'NC', 'ND', 'NE', 'NH',
  'NJ', 'NM', 'NV', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY',
] as const;

export interface AssetCare20PayCohort {
  gender: 'Male' | 'Female';
  age: number;
}

export interface AssetCare20PayCouple {
  primary: AssetCare20PayCohort;
  secondary: AssetCare20PayCohort;
}

/**
 * Both tobacco classes are covered for every cohort, so tobacco is not a filter.
 *
 * Single life: the two ages swept (M61, F70).
 *
 * Joint life: a PAIR, because the joint quote query matches both applicants --
 * primary against primary and secondary against secondary, or the two swapped
 * (see selectQuotesForClient). Only one couple was ever scraped, so a client who
 * matches one half of it and brings any other partner has no joint row to land
 * on. Gating on the primary applicant alone offered 20-Pay to, for example, a
 * Female-70 client with a Male-65 partner and then returned nothing.
 *
 * Order is irrelevant here: the query already tries both, so listing the couple
 * once is enough.
 */
export const assetCare20PaySingleCohorts: readonly AssetCare20PayCohort[] = [
  { gender: 'Male', age: 61 },
  { gender: 'Female', age: 70 },
] as const;

export const assetCare20PayJointCouples: readonly AssetCare20PayCouple[] = [
  { primary: { gender: 'Female', age: 70 }, secondary: { gender: 'Male', age: 61 } },
] as const;

// Mapping of funding policy types to their available states
export const PRODUCT_STATE_AVAILABILITY: Record<number, readonly string[]> = {
  2942: manhattanLifeOmniFlexStcStates, // FundingPolicyType.manhattanLifeOmniFlexStc
  2936: carescoutCareAssuranceStates, // FundingPolicyType.careScoutCareAssurance
  2919: careMattersTwoStates, // FundingPolicyType.careMattersTwo (careMattersStates + NY)
  2921: careMattersStates, // FundingPolicyType.jointCareMatters (no NY: single-life scrape only)
  2943: careMattersAnnuityStates, // FundingPolicyType.careMattersAnnuity
  2944: careMattersAnnuityStates, // FundingPolicyType.jointCareMattersAnnuity
  2918: assetCareStates, // FundingPolicyType.assetCare
  2920: assetCareStates, // FundingPolicyType.jointAssetCare
  2916: secureCareThreeStates, // FundingPolicyType.secureCareThree
  2941: secureCareFourStates, // FundingPolicyType.secureCareFour
  2917: moneyGuardFixedAdvantageStates, // FundingPolicyType.moneyGuardFixedAdvantage
  2914: nylAssetFlexStates, // FundingPolicyType.nylAssetFlex
} as const;

// Helper function to get available states for a funding policy type
export function getProductAvailableStates(
  fundingPolicyType: number,
): readonly string[] | undefined {
  return PRODUCT_STATE_AVAILABILITY[fundingPolicyType];
}

// Helper function to check if a product has specific state availability
export function hasProductSpecificStateAvailability(fundingPolicyType: number): boolean {
  return fundingPolicyType in PRODUCT_STATE_AVAILABILITY;
}

// Legacy export for backward compatibility (ShopInsuranceModal still uses this)
export const carescoutAvailableStates = carescoutCareAssuranceStates;

// States that are universally excluded (not approved to sell)
export const EXCLUDED_STATES = [] as const;

// OneAmerica expanded states: Category 2 states (except MT) that have been moved to Category 1
// These states are gated behind the oneAmericaExpandedStates feature flag
export const OA_EXPANDED_STATES = ['CT', 'DE', 'DC', 'ND', 'SC', 'SD'] as const;

// Get all states that have at least one product available
export function getAllSupportedStates(): string[] {
  const allStates = new Set<string>([
    ...carescoutCareAssuranceStates,
    ...careMattersTwoStates,
    ...careMattersAnnuityStates,
    ...assetCareStates,
    ...secureCareThreeStates,
    ...secureCareFourStates,
    ...moneyGuardFixedAdvantageStates,
    ...nylAssetFlexStates,
  ]);
  return Array.from(allStates).sort();
}

// Check if a state has any quote products available
export function isStateSupported(stateAbbreviation: string): boolean {
  return getAllSupportedStates().includes(stateAbbreviation);
}
